import { JSX } from 'react';
import { Link } from '@lmig/lmds-react-link';

// Only http(s)/mailto links and root-relative paths are rendered; anything else (e.g. "javascript:")
// is untrusted and shown as plain text instead of a clickable link.
const SAFE_ANSWER_LINK_SCHEME = /^(https?:|mailto:)/i;

export function isSafeAnswerLink(url: string): boolean {
  const trimmedUrl = url.trim();
  if (trimmedUrl.includes('\\')) {
    return false;
  }

  return (trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('//')) || SAFE_ANSWER_LINK_SCHEME.test(trimmedUrl);
}

const INLINE_MARKDOWN_PATTERN = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
const BOLD_PATTERN = /^\*\*([^*]+)\*\*$/;
const LINK_PATTERN = /^\[([^\]]+)\]\(([^)]+)\)$/;

// Renders "**bold**" and "[text](url)" markdown within a single line as React nodes
// (never raw HTML), so AI-generated text can't inject markup.
function renderInlineMarkdown(text: string, keyPrefix: string): (string | JSX.Element)[] {
  return text
    .split(INLINE_MARKDOWN_PATTERN)
    .filter((segment) => segment !== '')
    .map((segment, index) => {
      const key = `${keyPrefix}-${index}`;
      const boldMatch = BOLD_PATTERN.exec(segment);
      if (boldMatch) {
        return <strong key={key}>{boldMatch[1]}</strong>;
      }

      const linkMatch = LINK_PATTERN.exec(segment);
      if (linkMatch) {
        const [, linkText, href] = linkMatch;
        const trimmedHref = href.trim();
        if (isSafeAnswerLink(trimmedHref)) {
          return (
            <Link key={key} variant="inline" href={trimmedHref} target="_blank" rel="noopener noreferrer">
              {linkText}
            </Link>
          );
        }
        return linkText;
      }

      return segment;
    });
}

type AnswerLineType = 'ordered-item' | 'unordered-item' | 'paragraph';

interface IAnswerLine {
  type: AnswerLineType;
  content: string;
}

const ORDERED_LIST_PATTERN = /^\d+[.)]\s+(.*)$/;
const UNORDERED_LIST_PATTERN = /^[-*•]\s+(.*)$/;

function parseAnswerLines(answer: string): IAnswerLine[] {
  return answer
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const orderedMatch = ORDERED_LIST_PATTERN.exec(line);
      if (orderedMatch) {
        return { type: 'ordered-item' as const, content: orderedMatch[1] };
      }
      const unorderedMatch = UNORDERED_LIST_PATTERN.exec(line);
      if (unorderedMatch) {
        return { type: 'unordered-item' as const, content: unorderedMatch[1] };
      }
      return { type: 'paragraph' as const, content: line };
    });
}

// Groups the answer's lines into paragraphs and ordered/unordered lists, applying inline
// bold/link formatting within each.
export function renderFormattedAnswer(answer: string): JSX.Element[] {
  const lines = parseAnswerLines(answer);
  const blocks: JSX.Element[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (line.type === 'paragraph') {
      blocks.push(<p key={`p-${index}`}>{renderInlineMarkdown(line.content, `p-${index}`)}</p>);
      index += 1;
      continue;
    }

    const listType = line.type;
    const items: JSX.Element[] = [];
    while (index < lines.length && lines[index].type === listType) {
      items.push(<li key={`li-${index}`}>{renderInlineMarkdown(lines[index].content, `li-${index}`)}</li>);
      index += 1;
    }

    blocks.push(
      listType === 'ordered-item' ? (
        <ol key={`list-${index}`}>{items}</ol>
      ) : (
        <ul key={`list-${index}`}>{items}</ul>
      ),
    );
  }

  return blocks;
}
